import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  LinearProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useProjectSync } from '../../../../hooks/useProjectSync';
import { formatFileSize, formatDate } from '../../../../common/utils/fileUtils';
import { useFileStatus } from '../context/FileStatusContext';
import type { ProjectType as ProjectTypeFromHook } from '../../../../hooks/useProjectSync';
import ProjectFolderPrompt from '../components/ProjectFolderPrompt';

import { StatusBadge } from '@/common/components/status/StatusBadge';
import { LoadingSpinner } from '@/common/components/loading/LoadingSpinner';
import { FileSyncStatus } from '@/common/models/enums';
import { getFileNameParts } from '@/common/utils/filePathUtils';
import { useProjectVersionData } from '../hooks/useProjectVersionData';
import { useSyncStatusRefresh } from '../hooks/useSyncStatusRefresh';
import {
  findFileInVersion,
  getFileSizeFromVersion,
  getFileUploadedAtFromVersion,
} from '@/common/utils/fileInfoUtils';
import type { FileStatus } from '@/hooks/useProjectSync';
import { useProjectDetails } from '../context/ProjectDetailsContext';

const ProjectSyncTab: React.FC = () => {
  const {
    project,
    syncProjectDir,
    handleLocateProject,
    filesWithSyncRef,
    refetchProject,
  } = useProjectDetails();
  // Get latest version files using hook
  const { latestVersionFiles, refetchLatestVersion: refetchLatestVersionData } =
    useProjectVersionData(project || null);

  const { syncStatus, checkStatus: checkStatusBase } = useProjectSync(
    (project || null) as ProjectTypeFromHook | null,
    syncProjectDir,
    latestVersionFiles,
    async () => {
      // After sync completes, refetch project data first
      await refetchProject();

      // Then refetch the latest version data to get the new version files
      setTimeout(async () => {
        try {
          await refetchLatestVersionData();

          // Also refresh FilesWithSync component if available
          if (filesWithSyncRef?.current) {
            await filesWithSyncRef.current.refreshData();
          }
        } catch (error) {
          console.error(
            'Error refetching latest version data after sync:',
            error
          );
        }
      }, 500);
    }
  );

  // Wrap checkStatus to ensure it returns FileStatus[] | null with proper types
  const checkStatus = async (): Promise<FileStatus[] | null> => {
    const result = await checkStatusBase();
    if (!result || result.length === 0) return null;
    // Return as FileStatus[] - the status values will be cast by the hook
    return result as FileStatus[];
  };

  // Use shared file status context
  const { fileStatuses } = useFileStatus();

  // Use sync status refresh hook
  const { handleManualRefresh, isRefreshing } = useSyncStatusRefresh({
    projectDir: syncProjectDir,
    latestVersionFiles,
    checkStatus: async () => {
      const result = await checkStatus();
      return result;
    },
    refetchLatestVersionData: async () => {
      await refetchLatestVersionData();
    },
    filesWithSyncRef,
  });

  if (!syncProjectDir) {
    return (
      <Box p={3}>
        <ProjectFolderPrompt onLocateProject={handleLocateProject} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        '& .MuiPaper-root': {
          bgcolor: 'transparent',
          backgroundImage: 'none',
          boxShadow: 'none',
          border: 0,
          borderBottom: 1,
          borderColor: 'divider',
          borderRadius: 0,
        },
        '& .MuiTableContainer-root': { maxHeight: 480 },
        '& .MuiAccordionSummary-root': { borderRadius: 0 },
      }}
    >
      {/* Status Key */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
          },
          gridAutoRows: '1fr',
          gap: 1.5,
          mb: 3,
        }}
      >
        {[
          FileSyncStatus.UP_TO_DATE,
          FileSyncStatus.BEHIND,
          FileSyncStatus.NEWER,
          FileSyncStatus.NEW,
          FileSyncStatus.DELETED,
          FileSyncStatus.UNKNOWN,
        ].map((status) => (
          <Paper
            variant="outlined"
            key={status}
            sx={{ p: 2, height: '100%', boxSizing: 'border-box' }}
          >
            <StatusBadge status={status} showDescription={true} variant="box" />
          </Paper>
        ))}
      </Box>

      {/* Sync Progress */}
      {syncStatus.syncing && (
        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom>
            Refreshing Files... {syncStatus.progress}%
          </Typography>
          <LinearProgress
            data-cy="sync-progress-bar"
            variant="determinate"
            value={syncStatus.progress}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      )}

      {/* Error Message */}
      {syncStatus.error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {syncStatus.error}
        </Alert>
      )}

      {isRefreshing ? (
        <LoadingSpinner message="Refreshing file status..." />
      ) : (
        <Box>
          <Divider sx={{ mb: 3 }} />

          {/* Files Needing Attention */}
          {fileStatuses.some(
            (status) => status.status !== FileSyncStatus.UP_TO_DATE
          ) && (
            <Accordion
              defaultExpanded
              sx={{
                mb: 2,
                '&:before': { display: 'none' },
                boxShadow: 'none',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: 'studio.status.behind.bg', // Lighter warning color
                  borderTopLeftRadius: 1,
                  borderTopRightRadius: 1,
                  '&:hover': {
                    backgroundColor: 'studio.status.behind.bg',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ color: 'warning.main', fontWeight: 600 }}
                  >
                    Files Needing Attention (
                    {
                      fileStatuses.filter(
                        (status) => status.status !== FileSyncStatus.UP_TO_DATE
                      ).length
                    }
                    )
                  </Typography>
                  <Button
                    data-cy="sync-refresh-button"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent accordion from toggling
                      handleManualRefresh();
                    }}
                    disabled={isRefreshing}
                    sx={{ ml: 2 }}
                  >
                    {isRefreshing ? 'Refreshing...' : 'Refresh'}
                  </Button>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    maxHeight: 'calc(100vh - 600px)',
                    overflow: 'auto',
                    borderTop: 'none',
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          width="45%"
                          sx={{ backgroundColor: 'background.paper' }}
                        >
                          Local Files
                        </TableCell>
                        <TableCell
                          width="10%"
                          align="center"
                          sx={{ backgroundColor: 'background.paper' }}
                        >
                          Status
                        </TableCell>
                        <TableCell
                          width="45%"
                          sx={{ backgroundColor: 'background.paper' }}
                        >
                          Cloud Files
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fileStatuses
                        .filter(
                          (status) =>
                            status.status !== FileSyncStatus.UP_TO_DATE
                        )
                        .map((status) => (
                          <TableRow
                            key={status.fileName}
                            data-cy="sync-file-row"
                            data-file-name={status.fileName}
                            data-status={status.status}
                          >
                            {/* Local File Column */}
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              {status.isLocal ? (
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ wordBreak: 'break-word' }}
                                  >
                                    {getFileNameParts(status.fileName).name}
                                  </Typography>
                                  {getFileNameParts(status.fileName).path && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        display: 'block',
                                        wordBreak: 'break-word',
                                        opacity: 0.7,
                                      }}
                                    >
                                      {getFileNameParts(status.fileName).path}/
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.disabled"
                                  fontStyle="italic"
                                >
                                  (File not found locally)
                                </Typography>
                              )}
                            </TableCell>

                            {/* Status Column */}
                            <TableCell
                              align="center"
                              sx={{ verticalAlign: 'top' }}
                            >
                              <StatusBadge
                                status={status.status}
                                variant="inline"
                              />
                            </TableCell>

                            {/* Cloud File Column */}
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              {findFileInVersion(
                                latestVersionFiles,
                                status.fileName
                              ) ? (
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ wordBreak: 'break-word' }}
                                  >
                                    {getFileNameParts(status.fileName).name}
                                  </Typography>
                                  {getFileNameParts(status.fileName).path && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        display: 'block',
                                        wordBreak: 'break-word',
                                        opacity: 0.7,
                                      }}
                                    >
                                      {getFileNameParts(status.fileName).path}/
                                    </Typography>
                                  )}
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mt: 0.5 }}
                                  >
                                    {formatFileSize(
                                      getFileSizeFromVersion(
                                        latestVersionFiles,
                                        status.fileName
                                      )
                                    )}
                                    {' • '}
                                    Updated{' '}
                                    {formatDate(
                                      getFileUploadedAtFromVersion(
                                        latestVersionFiles,
                                        status.fileName
                                      )
                                    )}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.disabled"
                                  fontStyle="italic"
                                >
                                  (Not uploaded yet)
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Up-to-date Files */}
          {fileStatuses.some(
            (status) => status.status === FileSyncStatus.UP_TO_DATE
          ) && (
            <Accordion
              sx={{
                '&:before': { display: 'none' },
                boxShadow: 'none',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: 'studio.status.synced.bg', // Lighter success color
                  borderTopLeftRadius: 1,
                  borderTopRightRadius: 1,
                  '&:hover': {
                    backgroundColor: 'studio.status.synced.bg',
                  },
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'success.main', fontWeight: 600 }}
                >
                  Synchronized Files (
                  {
                    fileStatuses?.filter(
                      (status) => status?.status === FileSyncStatus.UP_TO_DATE
                    ).length
                  }
                  )
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    maxHeight: 'calc(100vh - 600px)',
                    overflow: 'auto',
                    borderTop: 'none',
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          width="45%"
                          sx={{ backgroundColor: 'background.paper' }}
                        >
                          Local Files
                        </TableCell>
                        <TableCell
                          width="10%"
                          align="center"
                          sx={{ backgroundColor: 'background.paper' }}
                        >
                          Status
                        </TableCell>
                        <TableCell
                          width="45%"
                          sx={{ backgroundColor: 'background.paper' }}
                        >
                          Cloud Files
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fileStatuses
                        .filter(
                          (status) =>
                            status?.status === FileSyncStatus.UP_TO_DATE
                        )
                        .map((status) => (
                          <TableRow key={status?.fileName}>
                            {/* Local File Column */}
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ wordBreak: 'break-word' }}
                                >
                                  {getFileNameParts(status?.fileName)?.name}
                                </Typography>
                                {getFileNameParts(status?.fileName)?.path && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      display: 'block',
                                      wordBreak: 'break-word',
                                      opacity: 0.7,
                                    }}
                                  >
                                    {getFileNameParts(status?.fileName)?.path}/
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>

                            {/* Status Column */}
                            <TableCell
                              align="center"
                              sx={{ verticalAlign: 'top' }}
                            >
                              <StatusBadge
                                status={
                                  status?.status || FileSyncStatus.UNKNOWN
                                }
                                variant="inline"
                              />
                            </TableCell>

                            {/* Cloud File Column */}
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ wordBreak: 'break-word' }}
                                >
                                  {getFileNameParts(status?.fileName)?.name}
                                </Typography>
                                {getFileNameParts(status?.fileName)?.path && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      display: 'block',
                                      wordBreak: 'break-word',
                                      opacity: 0.7,
                                    }}
                                  >
                                    {getFileNameParts(status?.fileName)?.path}/
                                  </Typography>
                                )}
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block', mt: 0.5 }}
                                >
                                  {formatFileSize(
                                    getFileSizeFromVersion(
                                      latestVersionFiles,
                                      status?.fileName || ''
                                    )
                                  )}
                                  {' • '}
                                  Updated{' '}
                                  {formatDate(
                                    getFileUploadedAtFromVersion(
                                      latestVersionFiles,
                                      status?.fileName || ''
                                    )
                                  )}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}

          {fileStatuses.length === 0 && (
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="center"
              py={4}
            >
              No files found in project.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ProjectSyncTab;
